import React, { useState, useEffect, useRef } from 'react';

const LoadingScreen = ({ onComplete }) => {
  const canvasRef = useRef(null);
  const [showText, setShowText] = useState(false);
  const [textVisible, setTextVisible] = useState(false);
  const [showCounter, setShowCounter] = useState(false);
  const [count, setCount] = useState(0);
  const [fadeOut, setFadeOut] = useState(false);
  const [hidden, setHidden] = useState(false);

  // Canvas drop + ripple animation
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const W = 300, H = 300;
    canvas.width = W;
    canvas.height = H;

    const cx = 150, startY = 70, endY = 150;
    const dropDuration = 600;
    let startTime = null;
    let phase = 'drop';
    let impactTime = null;
    let animId = null;

    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const elapsed = timestamp - startTime;
      ctx.clearRect(0, 0, W, H);

      if (phase === 'drop') {
        const t = Math.min(elapsed / dropDuration, 1);
        const eased = t * t;
        const y = startY + (endY - startY) * eased;
        // Teardrop shape
        ctx.beginPath();
        ctx.arc(cx, y, 18, 0, Math.PI * 2);
        ctx.fillStyle = '#0EA5E9';
        ctx.fill();
        if (t >= 1) { phase = 'ripple'; impactTime = timestamp; }
        animId = requestAnimationFrame(animate);
      } else if (phase === 'ripple') {
        const elapsed2 = timestamp - impactTime;
        [0, 200, 400].forEach(delay => {
          if (elapsed2 < delay) return;
          const t = Math.min((elapsed2 - delay) / 800, 1);
          ctx.beginPath();
          ctx.arc(cx, endY, t * 120, 0, Math.PI * 2);
          ctx.strokeStyle = `rgba(14,165,233,${0.8 * (1 - t)})`;
          ctx.lineWidth = 1.5;
          ctx.stroke();
        });
        if (elapsed2 < 1200) animId = requestAnimationFrame(animate);
      }
    };

    animId = requestAnimationFrame(animate);
    return () => { if (animId) cancelAnimationFrame(animId); };
  }, []);

  // Timers for text, counter, fadeout
  useEffect(() => {
    const t1 = setTimeout(() => { setShowText(true); setTimeout(() => setTextVisible(true), 50); }, 1000);
    const t2 = setTimeout(() => setShowCounter(true), 1300);
    const t3 = setTimeout(() => setFadeOut(true), 3300);
    const t4 = setTimeout(() => { setHidden(true); onComplete(); }, 4100);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); clearTimeout(t4); };
  }, [onComplete]);

  // Counter animation
  useEffect(() => {
    if (!showCounter) return;
    const dur = 2000;
    const start = performance.now();
    let raf;
    const animate = (now) => {
      const t = Math.min((now - start) / dur, 1);
      const eased = t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
      setCount(Math.round(eased * 100));
      if (t < 1) raf = requestAnimationFrame(animate);
    };
    raf = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(raf);
  }, [showCounter]);

  if (hidden) return null;

  return (
    <div
      data-testid="loading-screen"
      style={{
        position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
        background: 'rgba(245,245,247,0.15)',
        backdropFilter: 'blur(24px)', WebkitBackdropFilter: 'blur(24px)',
        zIndex: 99999,
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        opacity: fadeOut ? 0 : 1, transition: 'opacity 800ms ease',
      }}
    >
      <canvas ref={canvasRef} style={{ display: 'block', marginBottom: '-40px' }} />

      {showText && (
        <div style={{
          textAlign: 'center',
          opacity: textVisible ? 1 : 0,
          transition: 'opacity 600ms ease',
        }}>
          <div style={{
            fontFamily: 'Inter, sans-serif', fontWeight: 800, fontSize: '72px',
            letterSpacing: '0.2em', color: '#0A0A0A', lineHeight: 1,
          }}>ETERNA</div>
          <div style={{
            fontFamily: 'Inter, sans-serif', fontWeight: 200, fontSize: '18px',
            letterSpacing: '0.4em', color: '#6B6B6B', marginTop: '8px',
          }}>WATER</div>
        </div>
      )}

      {showCounter && (
        <div
          data-testid="loading-counter"
          style={{
            fontFamily: 'Inter, sans-serif', fontWeight: 200, fontSize: '48px',
            color: 'rgba(0,0,0,0.4)', marginTop: '32px',
          }}
        >
          {count}
        </div>
      )}
    </div>
  );
};

export default LoadingScreen;
